import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./../style/Businesslisting.css";

import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaXTwitter,
  FaYoutube,
  FaPinterestP,
} from "react-icons/fa6";

const API = "https://biz.dropyourbusiness.com/api";

const BusinessListing = () => {
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);

  // also keep the resolved numeric id for upload endpoints
  const [resolvedId, setResolvedId] = useState(null);

  const getUserFromStorage = () => {
    try {
      const storedUser = localStorage.getItem("user");
      const localbusinessId = localStorage.getItem("localbusinessId");
      const localBusinessSlug = localStorage.getItem("localBusinessSlug"); // NEW: slug
      const parsedUser = storedUser ? JSON.parse(storedUser) : {};

      return {
        user: parsedUser.user || null,
        localbusinessId: localbusinessId || null,
        localBusinessSlug: localBusinessSlug || null,
      };
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
      return { user: null, localbusinessId: null, localBusinessSlug: null };
    }
  };

  const userDetails = getUserFromStorage();

  // Prefer slug if we have it; else fall back to ID (from user or local)
  const initialIdentifier =
    userDetails?.localBusinessSlug ||
    userDetails?.user?.businessSlug ||
    userDetails?.user?.live_url ||
    userDetails?.user?.businessId ||
    userDetails?.localbusinessId ||
    null;

  useEffect(() => {
    const fetchBusinessDetails = async () => {
      if (!initialIdentifier) {
        toast.error("Business identifier not found.");
        setLoading(false);
        return;
      }

      setLoading(true);

      // helper to set state + cache
      const applyBusiness = (biz) => {
        setBusiness(biz || null);
        if (biz?.id) {
          setResolvedId(biz.id);
          localStorage.setItem("localbusinessId", String(biz.id));
        }
        if (biz?.live_url) {
          localStorage.setItem("localBusinessSlug", biz.live_url);
        }
      };

      // Try slug first if non-numeric, else try ID
      const isNumeric = /^\d+$/.test(String(initialIdentifier));
      try {
        let biz = null;

        if (!isNumeric) {
          // FETCH BY SLUG
          try {
            const r = await axios.get(
              `${API}/business/live/${encodeURIComponent(initialIdentifier)}`
            );
            biz =
              r?.data?.business ||
              r?.data?.data?.business ||
              r?.data?.data ||
              r?.data;
          } catch (e) {
            // will try ID fallback below if we have one
          }
        }

        if (!biz) {
          // FETCH BY ID (either identifier is numeric OR slug lookup failed and we still have an ID saved)
          const fallbackId = isNumeric
            ? String(initialIdentifier)
            : userDetails?.user?.businessId || userDetails?.localbusinessId;

          if (fallbackId) {
            const r2 = await axios.get(`${API}/business/${fallbackId}`);
            biz =
              r2?.data?.business ||
              r2?.data?.data?.business ||
              r2?.data?.data ||
              r2?.data;
          }
        }

        if (!biz) {
          toast.error("Failed to fetch business details.");
          applyBusiness(null);
        } else {
          applyBusiness(biz);
        }
      } catch (error) {
        console.error("Error fetching business details:", error);
        toast.error("Failed to fetch business details.");
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIdentifier]);

  const idForUploads = resolvedId; // always use numeric ID for upload endpoints

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!idForUploads) {
      toast.error("Business ID missing for upload.");
      return;
    }

    const formData = new FormData();
    formData.append("logo", file);

    try {
      setIsUploadingLogo(true);
      const response = await axios.post(
        `${API}/business/${idForUploads}/logo`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const cleanedLogoUrl = String(response.data.logo || "").replace(
        /\\/g,
        ""
      );
      setBusiness((prev) => ({
        ...(prev || {}),
        logo: cleanedLogoUrl || prev?.logo,
      }));
    } catch (err) {
      console.error("Error uploading logo:", err);
      toast.error("Logo upload failed.");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleBackgroundImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!idForUploads) {
      toast.error("Business ID missing for upload.");
      return;
    }

    const formData = new FormData();
    formData.append("images[]", file);

    try {
      setIsUploadingBackground(true);
      const response = await axios.post(
        `${API}/business/${idForUploads}/images`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const rawImageUrl = response?.data?.images?.[0] || "";
      const cleanedImageUrl = rawImageUrl.replace(/\\/g, "");

      setBusiness((prev) => ({
        ...(prev || {}),
        images: [cleanedImageUrl, ...((prev && prev.images) || [])],
      }));
    } catch (err) {
      console.error("Error uploading background image:", err);
      toast.error("Background image upload failed.");
    } finally {
      setIsUploadingBackground(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!business) {
    return <div>No business details available.</div>;
  }

  const {
    business_name,
    owner_name,
    category,
    sub_category,
    description,
    logo,
    images,
    address,
    social_accounts,
    status,
    business_time,
    live_url,
  } = business;

  const backgroundImage =
    images?.length > 0 ? images[0] : "/fallback-image.png";

  // Address may be plain strings or nested objects (normalize)
  const cityName = address?.city?.name || address?.city || "";
  const stateName = address?.state?.name || address?.state || "";
  const countryName = address?.country?.name || address?.country || "";

  const is247 =
    business_time?.is_24_7 === true ||
    business_time?.is_24_7 === 1 ||
    business_time?.is_24_7 === "1";

  const workingHours = business_time?.working_hours;

  const orderedDays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const label = (d) => d.charAt(0).toUpperCase() + d.slice(1);

  return (
    <div className="business-listing-page">
      <h1>Business Listing Details</h1>

      {status === "P" ? (
        <div className="approval-message">
          <p>
            Your business is under review. Once approved, it will be listed
            here.
          </p>
        </div>
      ) : status === "R" ? (
        <div className="rejection-message">
          <p>
            Your business was rejected. Please contact support for assistance.
          </p>
        </div>
      ) : (
        <div className="business-listing">
          <div className="overlay">
            <div
              className="profile-section"
              style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: "#fff",
              }}
            >
              <div className="logo-container">
                <img
                  src={logo}
                  alt={`${business_name} Logo`}
                  className="profile-logo"
                  onError={(e) => (e.currentTarget.src = "/fallback-image.png")}
                />
                <label htmlFor="upload-logo" className="upload-icon">
                  <span>+</span>
                </label>
                <input
                  type="file"
                  id="upload-logo"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleLogoUpload}
                />
              </div>
              {isUploadingLogo && <p>Uploading logo...</p>}

              <div className="homebanner-title">
                <div className="business-name">
                  <h2>{business_name}</h2>
                </div>
                <div className="business-title-below">
                  <p className="category">
                    {category?.name || category || "—"}{" "}
                    {sub_category
                      ? `- ${sub_category?.name || sub_category}`
                      : ""}
                  </p>
                  <label
                    htmlFor="upload-background"
                    className="upload-background"
                  >
                    <span>+</span>
                  </label>
                </div>
              </div>

              <input
                type="file"
                id="upload-background"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleBackgroundImageUpload}
              />
              {isUploadingBackground && <p>Uploading background image...</p>}
            </div>
          </div>

          {/* Optional: link to the public pretty URL */}
          {live_url && (
            <p style={{ marginTop: 12 }}>
              Public page:{" "}
              <a
                href={`/business/${live_url}`}
                target="_blank"
                rel="noreferrer"
              >
                /business/{live_url}
              </a>
            </p>
          )}

          <div className="description-box">
            <h3>Description</h3>
            <p className="description">{description}</p>
          </div>

          <div className="details-section">
            <div className="contact-box">
              <h2>Contact Information</h2>
              <p>
                <strong>Owner:</strong> {owner_name}
              </p>
              <p>
                <strong>Email:</strong> {address?.email}
              </p>
              <p>
                <strong>Contact:</strong> {address?.contact_1}
              </p>
              <p>
                <strong>Address:</strong>{" "}
                {`${address?.street_address_1 || ""}${
                  address?.street_address_2
                    ? ", " + address.street_address_2
                    : ""
                }${cityName ? ", " + cityName : ""}${
                  stateName ? ", " + stateName : ""
                }${countryName ? ", " + countryName : ""}${
                  address?.pincode ? " - " + address.pincode : ""
                }`}
              </p>
            </div>

            <div className="social-box">
              <h2>Social Accounts</h2>
              <ul>
                {social_accounts?.facebook_url && (
                  <li>
                    <a
                      href={social_accounts.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaFacebookF />
                    </a>
                  </li>
                )}
                {social_accounts?.instagram_url && (
                  <li>
                    <a
                      href={social_accounts.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaInstagram />
                    </a>
                  </li>
                )}
                {social_accounts?.linkedin_url && (
                  <li>
                    <a
                      href={social_accounts.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaLinkedinIn />
                    </a>
                  </li>
                )}
                {social_accounts?.x_url && (
                  <li>
                    <a
                      href={social_accounts.x_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaXTwitter />
                    </a>
                  </li>
                )}
                {social_accounts?.youtube_url && (
                  <li>
                    <a
                      href={social_accounts.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaYoutube />
                    </a>
                  </li>
                )}
                {social_accounts?.pinterest_url && (
                  <li>
                    <a
                      href={social_accounts.pinterest_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaPinterestP />
                    </a>
                  </li>
                )}
                {!social_accounts?.facebook_url &&
                  !social_accounts?.instagram_url &&
                  !social_accounts?.linkedin_url &&
                  !social_accounts?.x_url &&
                  !social_accounts?.youtube_url &&
                  !social_accounts?.pinterest_url && <li>Not available.</li>}
              </ul>
            </div>

            <div className="business-box">
              <h2>Business Timing</h2>

              {is247 ? (
                <p>Open 24/7</p>
              ) : typeof workingHours === "string" ? (
                <p>Working Hours: {workingHours}</p>
              ) : workingHours && typeof workingHours === "object" ? (
                <ul className="hours-list">
                  {orderedDays.map((day) => (
                    <li key={day}>
                      <strong>{label(day)}:</strong>{" "}
                      {workingHours[day] ? workingHours[day] : "Closed"}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Working Hours: Not provided</p>
              )}
            </div>
          </div>
          <ToastContainer />
        </div>
      )}
    </div>
  );
};

export default BusinessListing;
